
import { computed, defineComponent, onBeforeMount, PropType, reactive, ref } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import PhoneNumber from '@/common/components/UI/PhoneNumber/index.vue'

// types
import { CarrierTripartiteAgreementPayload, CarrierT } from '@/core/types/carriers.types'

// realisations
import { carriers } from '@/core/realisations/carriers.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'

// constants
import { VEHICLE_OWNER_OPTIONS } from '@/core/constants/couriers.constants'

// validation
import useVuelidate from '@vuelidate/core'
import { required, email, requiredIf, integer, between } from '@vuelidate/validators'
import { incorrectSymbolValidator } from '@/common/validators/common.validators'

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
        PhoneNumber,
    },
    props: {
        initialValue: {
            type: Object as PropType<CarrierT>,
            required: true,
        },
    },
    emits: ['cancel', 'submit'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const is_loading = ref(false)

        const state = reactive({
            name: null as string | null,
            bin: null as string | null,
            typeId: null as number | null,
            companyId: null as number | null,
            contactName: null as string | null,
            address: null as string | null,
            phoneNumber: null as string | null,
            contactEmail: null as string | null,
            carModel: null as string | null,
            carNumber: null as string | null,
            description: null as string | null,
            carColor: null as string | null,
            techPassport: null as string | null,
            releaseDate: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                name: { required: requiredIf(() => state.typeId !== 2), incorrectSymbolValidator },
                bin: { required },
                typeId: { required },
                companyId: { required },
                contactName: { required, incorrectSymbolValidator },
                address: { required },
                phoneNumber: { required },
                contactEmail: { required, email },
                carModel: { required },
                carNumber: { required },
                description: { required },
                carColor: { required },
                techPassport: { required },
                releaseDate: { required, integer, between: between(1900, 2500) },
            },
            state
        )

        const onCancel = () => {
            if (is_loading.value) return
            emit('cancel')
        }

        const onConfirm = async () => {
            if (is_loading.value) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                is_loading.value = true

                const payload: CarrierTripartiteAgreementPayload = {
                    name: state.name as string,
                    bin: state.bin as string,
                    typeId: state.typeId as number,
                    companyId: state.companyId as number,
                    contactName: state.contactName as string,
                    address: state.address as string,
                    phoneNumber: state.phoneNumber as string,
                    contactEmail: state.contactEmail as string,
                    carModel: state.carModel as string,
                    carNumber: state.carNumber as string,
                    description: state.description as string,
                    carColor: state.carColor as string,
                    techPassport: state.techPassport as string,
                    releaseDate: state.releaseDate as string,
                }

                await carriers.tripartiteAgreement(payload)

                notifications.notify({ type: 'success', title: '3-х сторонний договор отправлен' })
                emit('submit')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При отправке 3-х стороннего договора произошла ошибка',
                })
            } finally {
                is_loading.value = false
            }
        }

        const initializeState = () => {
            if (props.initialValue) {
                state.companyId = props.initialValue.id

                if (props.initialValue.vehicleOwner) {
                    state.name = props.initialValue.vehicleOwner.name
                    state.bin = props.initialValue.vehicleOwner.bin
                    state.typeId = props.initialValue.vehicleOwner.typeId
                    state.contactName = props.initialValue.vehicleOwner.contactName
                    state.address = props.initialValue.vehicleOwner.address
                    state.phoneNumber = props.initialValue.vehicleOwner.phoneNumber
                    state.contactEmail = props.initialValue.vehicleOwner.contactEmail
                    state.carModel = props.initialValue.vehicleOwner.carModel
                    state.carNumber = props.initialValue.vehicleOwner.carNumber
                    state.carColor = props.initialValue.vehicleOwner.carColor
                    state.description = props.initialValue.vehicleOwner.description
                    state.techPassport = props.initialValue.vehicleOwner.techPassport
                    state.releaseDate = props.initialValue.vehicleOwner.releaseDate
                }
            }
        }

        onBeforeMount(initializeState)

        const namePlaceholder = computed(() => {
            if (state.typeId === 2) {
                return 'ФИО перевозчика'
            }

            return 'Контактное имя'
        })

        const binPlaceholder = computed(() => {
            if (state.typeId === 2) {
                return 'ИИН'
            }

            return 'БИН'
        })

        return {
            is_loading,
            state,
            state_form$,
            onCancel,
            onConfirm,
            namePlaceholder,
            binPlaceholder,
            VEHICLE_OWNER_OPTIONS,
        }
    },
})
